import * as React from 'react';
import { LocationPageObject } from 'locations/lib/types';
import { graphql, PageProps } from 'gatsby';
import Layout from 'core/components/Layout';
import {
    PageContextWithMetaData,
    OgImageQuery,
    ResponsiveImageWithAltText,
} from 'lib/types';
import Breadcrumbs from 'core/components/Breadcrumbs';
import * as styles from 'locations/Location.module.scss';
import TeamMembers from 'locations/components/TeamMembers';
import { Employee } from '@bondvet/types/clinicTeam';
import BookingBanner from 'core/components/BookingBanner';
import LocationBookingCalendar from 'core/components/LocationBookingCalendar';
import ApolloProvider from 'core/components/ApolloProvider';
import LocationHero from 'locations/components/LocationHero';
import getDefaultData from 'data/pageDefaults/location';
import LocationCards from 'locations/components/LocationCards';
import GoogleReviews from 'locations/components/GoogleReviews';
import ServicesList from 'locations/components/ServicesList';
import useWindowSize from 'core/hooks/useWindowSize';
import { BREAKPOINT_XL_MIN } from 'core/constants';
import { parseCMSText } from 'lib/util';
import NearbyLocations from 'locations/components/NearbyLocations';
import LocationsProvider from 'core/components/LocationsProvider';
import FAQ from 'core/components/FAQ';
import CtaFooter from 'blocks/layouts/CtaFooter';
import EmailCapture from 'blocks/layouts/EmailCapture/EmailCapture';
import VirtualTour from 'locations/components/VirtualTour';
import Carousel from 'locations/components/Carousel';
import SEO from 'core/components/SEO';
import RichSnippets from 'locations/components/RichSnippets';
import LocationBookingPlaceholder from 'core/components/LocationBookingPlaceholder';

type LocationData = OgImageQuery & {
    location: LocationPageObject;
    defaultGoogleReviewImage: ResponsiveImageWithAltText;
    defaultVirtualTourImage: ResponsiveImageWithAltText;
    allClinicTeamMember: { nodes: ReadonlyArray<Employee> };
};

export default function LocationTemplate({
    data: {
        location,
        defaultGoogleReviewImage,
        defaultVirtualTourImage,
        allClinicTeamMember: { nodes: allTeamMembers },
        ogImage,
    },
    path,
    pageContext,
}: PageProps<LocationData, PageContextWithMetaData>) {
    const {
        pageData,
        heroImage: heroImageQuery,
        googleReviewImage: googleReviewImageQuery,
        virtualTourImage: virtualTourImageQuery,
        carouselImages,
    } = location;
    const { width } = useWindowSize();
    const isDesktop = width >= BREAKPOINT_XL_MIN;
    const fullWidthRef = React.useRef<HTMLDivElement>(null);
    const [fullWidthIsVisible, setFullWidthVisible] = React.useState(false);
    const lastScrollHeight = React.useRef(0);
    let heroImage;
    let virtualTourImage = defaultVirtualTourImage;
    let googleReviewImage = defaultGoogleReviewImage;
    const defaultData = getDefaultData(location?.name || '-');
    const [isLoadingCalendar, setIsLoadingCalendar] = React.useState(true);

    const teamMembers = allTeamMembers.filter(
        ({ pages }) => pages && pages?.indexOf('location') !== -1,
    );

    const { metaTitle, description } = location;

    if (pageData) {
        const heroImageObj = heroImageQuery?.image;
        if (heroImageObj?.mobile && heroImageObj?.desktop) {
            heroImage = heroImageObj as ResponsiveImageWithAltText;
        }
        const googleReviewImgObj = googleReviewImageQuery?.image;
        if (googleReviewImgObj?.mobile && googleReviewImgObj?.desktop) {
            googleReviewImage =
                googleReviewImgObj as ResponsiveImageWithAltText;
        }
        const virtualTourImgObj = virtualTourImageQuery?.image;
        if (virtualTourImgObj?.mobile && virtualTourImgObj?.desktop) {
            virtualTourImage = virtualTourImgObj as ResponsiveImageWithAltText;
        }
    }

    React.useEffect(() => {
        function scrollListener() {
            if (fullWidthRef.current) {
                const rect = fullWidthRef.current.getBoundingClientRect();
                const elemTop = rect.top;
                if (lastScrollHeight.current > elemTop) {
                    setFullWidthVisible(elemTop < window.innerHeight / 10);
                } else {
                    setFullWidthVisible(false);
                }
                lastScrollHeight.current = elemTop;
            } else {
                setFullWidthVisible(false);
            }
        }

        if (isDesktop) {
            window.addEventListener('scroll', scrollListener);

            return () => {
                window.removeEventListener('scroll', scrollListener);
            };
        }

        return () => {
            // nothing to do
        };
    }, [isDesktop]);

    const breadcrumbItems = [
        { link: '/regions', label: 'All Locations' },
        {
            link: `/regions/${location?.city?.slug}`,
            label: location?.city?.label,
        },
        { link: null, label: location.name },
    ];

    return (
        <>
            <SEO
                title={pageContext.title || metaTitle || ''}
                pathname={path}
                description={description || pageContext.description}
                ogImage={ogImage}
            />
            <Layout
                customAdbar={location.adbars}
                mainClasses={{ root: styles.main }}
                withFooterTopMargin={false}
                bookingCitySlug={location.city?.slug}
                bookingLocationSlug={location.shortSlug}
            >
                <div className={styles.page}>
                    <div className={styles.page__left_column}>
                        <Breadcrumbs
                            breadcrumbs={breadcrumbItems}
                            className={styles.breadcrumbs}
                        />
                        <LocationHero
                            location={location}
                            title={
                                pageData?.hero?.title ?? defaultData.hero.title
                            }
                            bio={parseCMSText(
                                pageData?.hero?.bio ||
                                    defaultData.hero.bio ||
                                    '',
                            )}
                            image={heroImage}
                            imageAlt={
                                heroImage?.alt || pageData?.hero?.heroImage?.alt
                            }
                        />
                        <hr className={styles.firstHr} />
                        {pageData?.teamMembers?.showMedicalTeam && (
                            <TeamMembers
                                title={
                                    pageData?.teamMembers?.title ??
                                    defaultData.teamMembers.title
                                }
                                teamMembers={teamMembers}
                            />
                        )}
                        <LocationCards
                            showGettingHere={
                                !!pageData?.cards?.train?.text ||
                                !!defaultData.cards.train.text
                            }
                            title={
                                pageData?.cards?.title ??
                                defaultData.cards.title
                            }
                            train={{
                                title:
                                    pageData?.cards?.train?.title ??
                                    defaultData.cards.train.title,
                                text: parseCMSText(
                                    pageData?.cards?.train?.text,
                                    defaultData.cards.train.text,
                                ),
                            }}
                            vet={{
                                title:
                                    pageData?.cards?.vet?.title ??
                                    defaultData.cards.vet.title,
                                text: parseCMSText(
                                    pageData?.cards?.vet?.text,
                                    defaultData.cards.vet.text,
                                ),
                            }}
                            virtualVisit={{
                                title:
                                    pageData?.cards?.virtualVisit?.title ??
                                    defaultData.cards.virtualVisit.title,
                                text: parseCMSText(
                                    pageData?.cards?.virtualVisit?.text,
                                    defaultData.cards.virtualVisit.text,
                                ),
                            }}
                        />
                        <hr className={styles.hr} />
                        <ServicesList
                            title={
                                pageData?.services?.title ??
                                defaultData.services.title
                            }
                            items={
                                pageData?.services?.items ??
                                defaultData.services.items
                            }
                        />
                        <hr className={styles.hr} />
                        <ServicesList
                            title={
                                pageData?.symptoms?.title ??
                                defaultData.symptoms.title
                            }
                            items={
                                pageData?.symptoms?.items ??
                                defaultData.symptoms.items
                            }
                        />
                    </div>
                    <div className={styles.page__right_column}>
                        {isLoadingCalendar && (
                            <div className={styles.bookingCalendar}>
                                <LocationBookingPlaceholder
                                    location={location}
                                />
                            </div>
                        )}
                        <div
                            className={styles.bookingCalendar}
                            style={{
                                visibility: isLoadingCalendar
                                    ? 'hidden'
                                    : 'visible',
                            }}
                        >
                            <ApolloProvider>
                                <LocationBookingCalendar
                                    location={location}
                                    onLoadingChange={setIsLoadingCalendar}
                                />
                            </ApolloProvider>
                        </div>
                    </div>
                    <div className={styles.page__full_width} ref={fullWidthRef}>
                        {location.googleReviews?.reviews && (
                            <GoogleReviews
                                googlePlaceId={
                                    location?.googleLocation?.metadata?.placeId
                                }
                                reviews={location.googleReviews?.reviews}
                                image={googleReviewImage}
                                imageAlt={
                                    pageData?.googleReviewImage?.alt ||
                                    googleReviewImage?.alt
                                }
                            />
                        )}
                        <ApolloProvider>
                            <LocationsProvider>
                                <NearbyLocations location={location} />
                            </LocationsProvider>
                        </ApolloProvider>
                        <EmailCapture
                            location={location}
                            title={
                                pageData?.emailCapture?.title ??
                                defaultData?.emailCapture?.title
                            }
                            text={parseCMSText(
                                pageData?.emailCapture?.text,
                                defaultData?.emailCapture?.text,
                            )}
                        />
                        {carouselImages && carouselImages.length > 0 && (
                            <Carousel
                                images={carouselImages}
                                subText={
                                    pageData?.carousel?.subText ??
                                    defaultData?.carousel?.subText ??
                                    ''
                                }
                            />
                        )}
                        <VirtualTour
                            title={
                                pageData?.virtualTour?.title ??
                                defaultData.virtualTour.title
                            }
                            text={parseCMSText(
                                pageData?.virtualTour?.text,
                                defaultData.virtualTour.text,
                            )}
                            tourSrc={
                                pageData?.virtualTour?.tourSrc ??
                                location.tourSrc
                            }
                            showImage={pageData?.virtualTour?.showImage}
                            image={virtualTourImage}
                            altText={
                                pageData?.virtualTour?.tourImage?.alt ??
                                virtualTourImage?.alt
                            }
                        />
                        <FAQ
                            faqItems={
                                pageData?.faq?.items || defaultData.faq.items
                            }
                            title={
                                pageData?.faq?.title || defaultData.faq.title
                            }
                            classes={{
                                root: styles.faqRoot,
                                title: styles.faqTitle,
                            }}
                        />
                        <CtaFooter
                            locationId={location._id}
                            content={
                                <p>
                                    We offer compassionate, thorough, and
                                    friendly care in a warm, comfortable
                                    environment. With convenient hours, same-day
                                    appointments, telehealth and a 24/7
                                    helpline, we’re always here when you need
                                    us.
                                </p>
                            }
                            classes={{
                                root: styles.ctaFooter,
                            }}
                        />
                        {isDesktop && location?.locationHours && (
                            <BookingBanner
                                locationId={location._id}
                                locationName={location.name || ''}
                                locationShortSlug={location.shortSlug || ''}
                                citySlug={location.city?.slug || ''}
                                openingHours={location.locationHours}
                                position="top"
                                visible={fullWidthIsVisible}
                            />
                        )}
                    </div>
                </div>
            </Layout>
            <RichSnippets
                data={location}
                schemaType="Location"
                faqItems={pageData?.faq?.items || defaultData.faq.items}
            />
        </>
    );
}

export const locationQuery = graphql`
    fragment LocationAsideImage on Upload {
        id
        file {
            ...TextImageLeftTitleAsideImage
        }
    }
    query LocationPageQuery($slug: String!, $ogImage: String) {
        location(slug: { eq: $slug }) {
            ...LocationData
            ...LocationHeroData
            ...RichSnippetsData
            metaTitle
            mainImage {
                ...LocationMainGalleryImage
            }
            carouselImages: images {
                ...LocationCarouselImage
            }
            cardText {
                booking
                service
                vet
            }
            texts {
                virtualTourTitle
                virtualTour
                textImageLeftTitle
                textImageLeftText
            }
            treatments {
                title
                categories {
                    title
                    services {
                        label
                        to
                    }
                }
            }
            tourSrc
            reviewLinks {
                google
            }
            googleReviews {
                reviews {
                    comment
                    rating
                    reviewer {
                        displayName
                    }
                }
            }
            googleReviewsLocations {
                _id
                name
                googleReviews {
                    averageRating
                    bestRating
                    reviews {
                        comment
                        rating
                        reviewer {
                            displayName
                            profilePhotoUrl
                        }
                    }
                    totalReviewCount
                    worstRating
                }
            }
            googleLocation {
                metadata {
                    mapsUrl
                    newReviewUrl
                    placeId
                }
            }
            adbars {
                title
                startTime
                endTime
            }
            pageData
            heroImage: pageImage(slug: "hero.heroImage") {
                slug
                alt
                image {
                    mobile: file {
                        ...MobileLocationHeroImage
                    }
                    desktop: file {
                        ...DesktopLocationHeroImage
                    }
                }
            }
            googleReviewImage: pageImage(slug: "googleReviewImage") {
                slug
                alt
                image {
                    mobile: file {
                        ...GoogleReviewsMobileImage
                    }
                    desktop: file {
                        ...GoogleReviewsDesktopImage
                    }
                }
            }
            virtualTourImage: pageImage(slug: "virtualTour.tourImage") {
                alt
                slug
                image {
                    mobile: file {
                        ...VirtualTourMobileImage
                    }
                    desktop: file {
                        ...VirtualTourDesktopImage
                    }
                }
            }
        }
        defaultGoogleReviewImage: file(
            relativePath: { eq: "google-reviews-bg.jpg" }
        ) {
            ...GoogleReviewsResponsiveImage
        }
        defaultVirtualTourImage: file(
            relativePath: { eq: "BondVet_SignageResized-5.jpg" }
        ) {
            ...VirtualTourResponsiveImage
        }
        ogImage: upload(imageId: { eq: $ogImage }) {
            ...OgImage
        }
        allClinicTeamMember(
            filter: {
                isActive: { eq: true }
                display: { eq: true }
                pages: { in: ["location"] }
                locations: { elemMatch: { slug: { eq: $slug } } }
            }
        ) {
            nodes {
                ...TeamMembersFragment
            }
        }
    }
`;
